import { CheckCircle2, XCircle, Check, X, BookOpen, Receipt, Lightbulb } from "lucide-react";

const Frame = ({ title, badge, badgeClass, children, testid }) => (
  <figure className="border border-stone-200 bg-white shadow-[6px_6px_0px_#e7e5e4]" data-testid={testid}>
    <div className="flex items-center gap-2 border-b border-stone-200 bg-stone-50 px-4 py-2.5">
      <span className="h-2 w-2 rounded-full bg-stone-300" />
      <span className="h-2 w-2 rounded-full bg-stone-300" />
      <span className="h-2 w-2 rounded-full bg-stone-300" />
      <span className="ml-2 font-mono-data text-[11px] text-slate-400">{title}</span>
    </div>
    <div className={`flex items-center gap-2 px-5 py-3 font-mono-data text-xs font-semibold uppercase tracking-wider ${badgeClass}`}>
      {badge}
    </div>
    <div className="px-5 py-5">{children}</div>
  </figure>
);

const Row = ({ dr, account, amount, indent }) => (
  <tr className="border-b border-stone-100">
    <td className="w-8 py-1.5 font-semibold">{dr ? "Dr" : "Cr"}</td>
    <td className={`py-1.5 ${indent ? "pl-5" : ""}`}>{account}</td>
    <td className="py-1.5 text-right">{amount}</td>
  </tr>
);

export const ResultPreview = () => (
  <div className="grid gap-10 lg:grid-cols-3">
    <Frame testid="preview-wrong" title="ledgercheck.in / analyze"
      badge={<><XCircle className="h-4 w-4" /> Entry appears incorrect</>}
      badgeClass="bg-red-50 text-red-800 border-b border-red-200">
      <table className="w-full border-collapse font-mono-data text-xs">
        <tbody>
          <Row dr account="Repairs Expense A/c" amount="₹4,00,000.00" />
          <Row account="Bank A/c" amount="₹4,00,000.00" indent />
        </tbody>
      </table>
      <ul className="mt-4 space-y-1.5">
        <li className="flex items-start gap-2 text-[11px] leading-relaxed text-slate-600">
          <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center bg-emerald-700 text-white"><Check className="h-2.5 w-2.5" /></span>
          Double entry balance
        </li>
        <li className="flex items-start gap-2 text-[11px] leading-relaxed text-slate-600">
          <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center bg-red-700 text-white"><X className="h-2.5 w-2.5" /></span>
          Classification — a new lift shaft is not repairs
        </li>
      </ul>
      <p className="mt-4 text-[11px] leading-relaxed text-slate-600">
        Balanced, but wrongly classified. Expenditure that extends the asset's life must be capitalised, not expensed.
      </p>
      <p className="overline mt-4 flex items-center gap-1.5 text-slate-400"><BookOpen className="h-3 w-3" /> Ind AS 16, Para 13</p>
    </Frame>

    <Frame testid="preview-right" title="ledgercheck.in / analyze"
      badge={<><CheckCircle2 className="h-4 w-4" /> Entry appears correct</>}
      badgeClass="bg-emerald-50 text-emerald-800 border-b border-emerald-200">
      <table className="w-full border-collapse font-mono-data text-xs">
        <tbody>
          <Row dr account="Machinery A/c" amount="₹5,00,000.00" />
          <Row dr account="Input CGST A/c" amount="₹45,000.00" />
          <Row dr account="Input SGST A/c" amount="₹45,000.00" />
          <Row account="Bank A/c" amount="₹5,90,000.00" indent />
        </tbody>
      </table>
      <div className="mt-4 grid grid-cols-2 gap-3 border-y border-stone-100 py-3 font-mono-data text-[11px]">
        <span>Dr total <b>₹5,90,000</b></span>
        <span className="text-right">Cr total <b>₹5,90,000</b></span>
      </div>
      <p className="mt-4 text-[11px] leading-relaxed text-slate-600">
        Capitalised correctly and the GST is split into eligible input credit, so nothing is loaded into cost.
      </p>
      <p className="overline mt-4 flex items-center gap-1.5 text-slate-400"><Receipt className="h-3 w-3" /> ITC eligible, s.16 CGST Act</p>
    </Frame>

    <Frame testid="preview-suggested" title="ledgercheck.in / analyze"
      badge={<><Lightbulb className="h-4 w-4" /> Suggested entry</>}
      badgeClass="bg-[#0f172a] text-white">
      <p className="font-mono-data text-[11px] italic leading-relaxed text-slate-500">
        "Paid ₹60,000 office rent for June by cheque, 18% GST, TDS u/s 194-I"
      </p>
      <table className="mt-4 w-full border-collapse font-mono-data text-xs">
        <tbody>
          <Row dr account="Rent Expense A/c" amount="₹60,000.00" />
          <Row dr account="Input CGST A/c" amount="₹5,400.00" />
          <Row dr account="Input SGST A/c" amount="₹5,400.00" />
          <Row account="TDS Payable A/c" amount="₹6,000.00" indent />
          <Row account="Bank A/c" amount="₹64,800.00" indent />
        </tbody>
      </table>
      <p className="mt-4 text-[11px] leading-relaxed text-slate-600">
        TDS at 10% is deducted on the rent, not on the GST — so the bank leg is ₹64,800.
      </p>
    </Frame>
  </div>
);
